import type { OwnedAccount } from '@hotjar/account';
import type { ComponentProps } from 'react';
import { Button, Modal } from '@hotjar/hotsauce';
import { useTranslationWithPrefix } from '@hotjar/translations';
import { closedModal, loadedModal, trackClick } from '@hotjar/webapp/tracking/common';
import { extractAccountGroupingProps } from '@hotjar/webapp/tracking/utils';
import { ChurnSurvey } from '@hotjar/webapp/features/Billing/ChurnSurvey';
import { useEffectOnceWithDependencies } from '@hotjar/webapp/hooks/useEffectOnceWithDependencies';
import { uniqBy } from 'lodash-es';
import { PlanChangeList } from '../SummaryOfChanges/PlanChangeList';
import { S } from '../styles';
import { MODAL_NAME } from '../types';

type Props = ComponentProps<typeof PlanChangeList> & {
  onCancel(): void;
  onSubmit(): void;
  account: OwnedAccount;
};

const CHURN_FORM_NAME = 'churn-form';

export function ChurnSurveyModal({ onCancel, onSubmit, changeList, account }: Props): JSX.Element {
  const { t: tChurnSurvey } = useTranslationWithPrefix('billing:churn-survey');
  const { t: tCommon } = useTranslationWithPrefix('common:actions');

  useEffectOnceWithDependencies(
    () => {
      loadedModal(MODAL_NAME.churnSurvey, extractAccountGroupingProps(account), {
        modal_site_count: uniqBy(changeList, ({ siteLink }) => siteLink).length,
      });
    },
    [account],
    () => account !== undefined,
  );

  function handleCancel() {
    closedModal(MODAL_NAME.multiSkuChurnSurvey, extractAccountGroupingProps(account));
    onCancel();
  }

  function handleSubmit() {
    trackClick({
      name: 'Confirm change',
      uiPosition: `${MODAL_NAME.churnSurvey} modal`,
      groupData: extractAccountGroupingProps(account),
    });
    onSubmit();
  }

  return (
    <Modal contentLabel={tChurnSurvey('title')} size="m" isOpen onClose={handleCancel}>
      <Modal.Header title={tChurnSurvey('title')} />
      <S.ModalBody>
        <S.PlanChangeListContainer>
          <PlanChangeList changeList={changeList} isFirstColumnNarrow />
          <ChurnSurvey
            onPlanChangeSubmit={handleSubmit}
            formId={CHURN_FORM_NAME}
            style={{ padding: 0 }}
          />
        </S.PlanChangeListContainer>
      </S.ModalBody>
      <Modal.Footer>
        <Button kind="ghost" onClick={handleCancel}>
          {tCommon('cancel')}
        </Button>
        <Button form={CHURN_FORM_NAME} type="submit">
          {tCommon('continue')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
